/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useQuery } from "@tanstack/react-query";
import { Button, FontAwesomeIcon, Stack } from "components";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
import useEventRouting from "hooks/useEventRouting";
import { getProfile } from "lib/userApi";
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import { useAuth } from "state/AuthContext";
import { useInvitation } from "state/InvitationContext";
import { useOrgData } from "state/OrgDataContext";
import { useUI } from "state/UIContext";
import { ScreenPage } from "templates";
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import { useLocalStorage } from "usehooks-ts";
import useRegisterLinkNavigation from "./useRegisterLinkNavigation";

const LoginPage = () => {
  const { t } = useTranslation();
  const { dataContent: EVENT_CONTENT } = useOrgData();
  const loginNavigator = useRegisterLinkNavigation();
  const { isLoggedIn } = useAuth();
  const { invitation } = useInvitation({
    fetchInvitation: isLoggedIn,
    hardFetch: true,
  });
  const { colorScheme } = useUI();
  const EVENT_ASSETS = useAppAssetsWithSrc();
  const { navigateWithEvent, eventId } = useEventRouting();
  const [id] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const { data: userData } = useQuery({
    queryKey: ["get-profile", id],
    queryFn: () => getProfile(id, "user", eventId),
    enabled: !!id,
  });

  const routeToNavigate = () => {
    if (
      isLoggedIn &&
      invitation &&
      userData &&
      !userData.data.is_form_completed
    ) {
      navigateWithEvent(ROUTES.onboard.invitation);
      return;
    }
    loginNavigator();
  };

  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.src
          : EVENT_ASSETS.login_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.fallbackSrc
          : EVENT_ASSETS.login_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.src
          : EVENT_ASSETS.login_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.fallbackSrc
          : EVENT_ASSETS.login_BG_mobileDark.fallbackSrc
      }
    >
      <h2 className="font-bold mb-4 text-3xl md:text-4xl capitalize -text--theme-colors-text-title">
        {t("auth.login.welcomeTitle")}
      </h2>
      <p className="-text--theme-colors-text-body">
        {EVENT_CONTENT.login.selectEventInstruction}
      </p>
      <Stack className="mt-8">
        <Button onClick={routeToNavigate} variant="primary">
          <div className="flex items-center gap-x-s10">
            {t("auth.login.continueButtonLabel")}
            <FontAwesomeIcon name="fa-chevron-right" />
          </div>
        </Button>
      </Stack>
    </ScreenPage>
  );
};
export default LoginPage;
